/**
 * Enrollments service — Firestore CRUD for the `enrollments` collection.
 *
 * An enrollment is the join between a student and a course. A student can hold
 * many enrollments at once; each is an independent document with its own mentor,
 * status, start date and progress, so enrolling in a new course never touches
 * the existing ones.
 *
 * An enrollment document:
 *   {
 *     studentId, studentName, studentEmail,   // denormalized from students/{id}
 *                              // studentEmail (lowercased) makes a student's own
 *                              // reads query-provable in the security rules.
 *     courseId, courseName,    // denormalized from courses/{id} for table display
 *     mentorId,                // per-enrollment mentor (independent of student.mentorId)
 *     status,                  // 'active' | 'completed' | 'paused' | 'cancelled'
 *     startDate: Timestamp,    // when the course began for this student
 *     progress,                // 0..100 percent — auto-synced from session logs by
 *                              // the onSessionLogWritten Cloud Function when the
 *                              // course has syllabus modules; admin-editable fallback
 *     currentModule,           // a value from the course's modules[] (or free text)
 *     currentLesson,           // free-text lesson label within the module
 *     completedModules,        // string[] — server-maintained: syllabus modules the
 *     progressSyncedAt,        // mentor has ticked off across submitted session logs
 *     completedSimulations,    // count of simulations finished for this course
 *     badges: string[],        // certificates / badges earned
 *     notes,                   // admin notes
 *     createdAt, updatedAt     // server timestamps
 *   }
 *
 * Progress and currentModule are kept in sync automatically by the
 * onSessionLogWritten Cloud Function (recomputed from the mentor's submitted
 * session logs) for courses that have syllabus modules; an admin edit still
 * works and is corrected on the next sync. Simulations/badges remain
 * admin-editable, displayed read-only to the student. `completedModules` /
 * `progressSyncedAt` are deliberately NOT in sanitize() so client writes can
 * never clobber the server-maintained values.
 */
import firestoreService, { orderBy, where, Timestamp } from './firestore.service';

const ENROLLMENTS = 'enrollments';

export const ENROLLMENT_STATUS = Object.freeze({
  ACTIVE: 'active',
  COMPLETED: 'completed',
  PAUSED: 'paused',
  CANCELLED: 'cancelled',
});

/** Convert a date string (or Date) into a Firestore Timestamp. */
function toTimestamp(value) {
  if (!value) return null;
  if (value instanceof Timestamp) return value;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : Timestamp.fromDate(date);
}

/** Clamp a value into the 0..100 progress range (defaults to 0). */
function clampProgress(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, Math.round(n)));
}

/** Whitelist the fields we persist so stray form state never leaks in. */
function sanitize(input = {}) {
  const sims = Number(input.completedSimulations);
  return {
    studentId: input.studentId || '',
    studentName: (input.studentName || '').trim(),
    studentEmail: (input.studentEmail || '').trim().toLowerCase(),
    courseId: input.courseId || '',
    courseName: (input.courseName || '').trim(),
    mentorId: input.mentorId || '',
    status: input.status || ENROLLMENT_STATUS.ACTIVE,
    startDate: toTimestamp(input.startDate),
    progress: clampProgress(input.progress),
    currentModule: (input.currentModule || '').trim(),
    currentLesson: (input.currentLesson || '').trim(),
    completedSimulations: Number.isFinite(sims) && sims >= 0 ? Math.round(sims) : 0,
    badges: Array.isArray(input.badges) ? input.badges.map((b) => (b || '').trim()).filter(Boolean) : [],
    notes: (input.notes || '').trim(),
  };
}

export const enrollmentsService = {
  /** Admin: realtime subscription to every enrollment, newest first. */
  subscribe(onData, onError) {
    return firestoreService.subscribeCollection(
      ENROLLMENTS,
      [orderBy('createdAt', 'desc')],
      onData,
      onError
    );
  },

  list() {
    return firestoreService.getCollection(ENROLLMENTS, [orderBy('createdAt', 'desc')]);
  },

  /** Server-side total count (aggregation — no document downloads). */
  count() {
    return firestoreService.count(ENROLLMENTS);
  },

  /**
   * Realtime subscription to one student's enrollments. Filtered server-side by
   * `studentId` (query-provable against the rules) and sorted client-side by
   * start date so no composite index is needed. Powers the student dashboard.
   */
  subscribeByStudent(studentId, onData, onError) {
    return firestoreService.subscribeCollection(
      ENROLLMENTS,
      [where('studentId', '==', studentId)],
      (rows) => onData(sortByStart(rows)),
      onError
    );
  },

  /** Realtime subscription to the enrollments a single mentor is assigned to. */
  subscribeByMentor(mentorId, onData, onError) {
    return firestoreService.subscribeCollection(
      ENROLLMENTS,
      [where('mentorId', '==', mentorId)],
      (rows) => onData(sortByStart(rows)),
      onError
    );
  },

  get(id) {
    return firestoreService.getDocument(ENROLLMENTS, id);
  },

  /**
   * Enrol a student in a course. The enrollment's `mentorId` is what pairs this
   * student with that mentor — the snapshot that lets the mentor read the
   * student record at all is re-derived by the `onEnrollmentLinksChanged`
   * Cloud Function, so no caller has to remember to refresh it.
   */
  create(data) {
    return firestoreService.addDocument(ENROLLMENTS, sanitize(data));
  },

  update(id, data) {
    return firestoreService.updateDocument(ENROLLMENTS, id, sanitize(data));
  },

  /**
   * Assign (or clear) the mentor for one course without touching the rest of
   * the enrollment. This is *the* student↔mentor pairing: a student holds one
   * enrollment per course, so assigning here is what lets them learn different
   * courses with different mentors.
   *
   * @param {string} id
   * @param {string} mentorId  '' clears the assignment
   */
  assignMentor(id, mentorId) {
    return firestoreService.updateDocument(ENROLLMENTS, id, { mentorId: mentorId || '' });
  },

  /**
   * Flip an enrollment to a new lifecycle status without touching other fields.
   * Cancelling is what withdraws the mentor's access to the student record.
   */
  setStatus(id, status) {
    return firestoreService.updateDocument(ENROLLMENTS, id, { status });
  },

  remove(id) {
    return firestoreService.deleteDocument(ENROLLMENTS, id);
  },
};

/** Chronological sort by `startDate` (newest first), tolerant of missing values. */
function sortByStart(rows) {
  return [...rows].sort((a, b) => {
    const at = a.startDate?.toDate?.()?.getTime?.() ?? 0;
    const bt = b.startDate?.toDate?.()?.getTime?.() ?? 0;
    return bt - at;
  });
}

export default enrollmentsService;
